import { Dayjs } from "dayjs";
import {
  ConfusionMatrixEntry,
  useGetLabellerAccuracyStatsQuery,
  useGetLabellerMatchedEventsQuery,
} from "src/store/api/generatedApi";
import { AccuracyEventRow } from "./AccuracyEventsTable";
import { AccuracyMetric } from "./utils";

const EMPTY_CONFUSION_MATRIX: ConfusionMatrixEntry[] = [];

interface Args {
  tenantName: string;
  labellerId: string;
  location: string;
  metric: AccuracyMetric;
  predicted: string;
  actual: string;
  start: Dayjs | null;
  end: Dayjs | null;
  page: number;
  pageSize: number;
}

const matrixEntriesForMetric = (
  metric: AccuracyMetric,
  materialEntries: ConfusionMatrixEntry[],
  areaEntries: ConfusionMatrixEntry[]
): ConfusionMatrixEntry[] => {
  if (metric === "material") return materialEntries;
  if (metric === "area") return areaEntries;
  return [];
};

const totalRowCountForMetric = (
  metric: AccuracyMetric,
  predicted: string,
  actual: string,
  matrixEntries: ConfusionMatrixEntry[],
  matchedEvents: number
): number => {
  if (metric === "timing") return matchedEvents;
  if (predicted === "" || actual === "") return 0;
  return (
    matrixEntries.find(
      (entry) => entry.predicted === predicted && entry.actual === actual
    )?.count ?? 0
  );
};

export const useLabellerAccuracyData = ({
  tenantName,
  labellerId,
  location,
  metric,
  predicted,
  actual,
  start,
  end,
  page,
  pageSize,
}: Args) => {
  const skip = !labellerId || !location;
  const startIso = start?.toISOString();
  const endIso = end?.toISOString();

  const {
    data: accuracyStats,
    isFetching: isFetchingStats,
    error: statsError,
  } = useGetLabellerAccuracyStatsQuery(
    {
      tenantName,
      labellerId,
      location,
      start: startIso,
      end: endIso,
    },
    { skip }
  );

  const {
    data: matchedEvents,
    isFetching: isFetchingRows,
    error: rowsError,
  } = useGetLabellerMatchedEventsQuery(
    {
      tenantName,
      labellerId,
      location,
      metric,
      predicted: predicted || undefined,
      actual: actual || undefined,
      start: startIso,
      end: endIso,
      page,
      pageSize,
    },
    {
      skip:
        skip || !(metric === "timing" || (predicted !== "" && actual !== "")),
    }
  );

  const matrixEntries = matrixEntriesForMetric(
    metric,
    accuracyStats?.material_confusion_matrix ?? EMPTY_CONFUSION_MATRIX,
    accuracyStats?.area_confusion_matrix ?? EMPTY_CONFUSION_MATRIX
  );

  const eventRows: AccuracyEventRow[] =
    matchedEvents?.rows.map((row) => ({
      id: `${row.labeller_event_id}-${row.verified_event_id}`,
      labellerEventStart: row.labeller_start,
      predictedLabel:
        metric === "area" ? row.predicted_area : row.predicted_material,
      actualLabel: metric === "area" ? row.actual_area : row.actual_material,
      iou: row.timing_iou,
    })) ?? [];

  const totalRowCount = totalRowCountForMetric(
    metric,
    predicted,
    actual,
    matrixEntries,
    accuracyStats?.matched_events ?? 0
  );

  return {
    matrixEntries,
    eventRows,
    totalRowCount,
    isFetching: isFetchingStats || isFetchingRows,
    error: statsError ?? rowsError,
  };
};
